import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    createStyles,
    makeStyles,
    useMediaQuery
} from '@material-ui/core';

import {
    IResumeOffre,
    TypeAssurance,
    hasAlreadyAssurance,
    hasSoumissionAssurance,
    IAssureResume,
    ActionsAssurance,
    obtenirResumeAssuranceTitreSection,
    ParcoursAssurance,
    obtenirLibellePaiementInvalidite
} from '../../hooks';
import { Typography, Variant, Police, ContainerMui } from '../../styles';
import { ProtectionModal } from '..';
import { ResumeAjoutAssurance } from './resumeAjoutAssurance/ResumeAjoutAssurance';
import { EditAssuranceButton } from './editAssuranceButton/EditAssuranceButton';

const useStyles = makeStyles(() =>
    createStyles({
        fontSize16: {
            fontSize: '16px !important'
        }
    })
);

export interface IResumeAssuranceProps {
    resumeOffre: IResumeOffre;
    assures: Array<IAssureResume>;
    isAssuranceAdmissible: boolean;
    isEditButtonDisplayed: boolean;
    isNoAssuranceDisplayed: boolean;
    isModalProtectionVisible?: boolean; // ont affiche cette modale dans le parcours Martine
    parcoursAssurance?: ParcoursAssurance;
    onEditAssurance: (action: ActionsAssurance) => void;
}

export const ResumeAssurance: React.FC<IResumeAssuranceProps> = ({
    resumeOffre,
    assures = [],
    isAssuranceAdmissible,
    isEditButtonDisplayed,
    isNoAssuranceDisplayed,
    isModalProtectionVisible,
    parcoursAssurance,
    onEditAssurance
}) => {
    const classes = useStyles();

    const [t] = useTranslation(['ResumeAssurance']);
    const isMobile = useMediaQuery('(max-width:640px)');

    const hasAssurance = hasAlreadyAssurance(
        resumeOffre?.assurance?.codeAssurance
    );

    const renderAssuranceAssure = () => {
        return (
            <>
                {hasAssurance && (
                    <>
                        <Typography variant={Variant.p} marginBottom="0.25rem">
                            {resumeOffre?.assurance?.typeAssurance ===
                            TypeAssurance.AP
                                ? t('resumeAssuranceTauxInteretAdditionnel')
                                : t('resumeAssuranceTauxInteretAdditionnelMat')}
                        </Typography>
                        <Typography variant={Variant.p} marginBottom="1rem">
                            <strong>
                                {t('Number:percent3Decimal', {
                                    value: resumeOffre?.offreChoisie
                                        ?.tauxAdditionnelAssurance
                                })}
                            </strong>
                        </Typography>
                    </>
                )}
                {resumeOffre?.assurance?.listeAssure &&
                    resumeOffre?.assurance?.listeAssure?.map((assure, key) => {
                        return (
                            <div key={key}>
                                <Typography
                                    variant={Variant.h4}
                                    marginBottom="1rem"
                                    className={classes.fontSize16}
                                >
                                    {assure?.prenom.toUpperCase() +
                                        ' ' +
                                        assure?.nom.toUpperCase()}
                                </Typography>
                                <Typography
                                    variant={Variant.h5}
                                    marginBottom="0.25rem"
                                    police={Police.normal}
                                    className={classes.fontSize16}
                                >
                                    {t('resumeAssuranceVieTitle')}
                                </Typography>
                                {assure?.indicateurEligibiliteAssuranceVie ? (
                                    <Typography
                                        variant={Variant.p}
                                        marginBottom="1rem"
                                    >
                                        <strong>
                                            {t('Number:percentNoDecimal', {
                                                value: assure?.pctCouvertureAssuranceVie
                                            })}
                                        </strong>
                                        {resumeOffre?.assurance
                                            ?.typeAssurance ===
                                        TypeAssurance.AP_MAT
                                            ? t('resumeAssuranceVieApMatTexte')
                                            : t('resumeAssuranceVieTexte')}
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant={Variant.p}
                                        marginBottom="1rem"
                                        dataTestId="text_fin_assurance_vie_resume"
                                    >
                                        {t('resumeAssuranceVieDisabledTexte')}
                                    </Typography>
                                )}
                                <Typography
                                    variant={Variant.h5}
                                    marginBottom="0.25rem"
                                    police={Police.normal}
                                    className={classes.fontSize16}
                                >
                                    {t('resumeAssuranceInvaliditeTitle')}
                                </Typography>
                                {assure?.indicateurEligibiliteAssuranceInvalidite ? (
                                    <Typography
                                        variant={Variant.p}
                                        hideMarginBottom
                                    >
                                        <strong>
                                            {t('Number:percentNoDecimal', {
                                                value: assure?.pctCouvertureAssuranceInvalidite
                                            })}
                                        </strong>
                                        {t(
                                            obtenirLibellePaiementInvalidite(
                                                resumeOffre
                                            )
                                        )}
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant={Variant.p}
                                        hideMarginBottom
                                        dataTestId="text_fin_assurance_invalidite_resume"
                                    >
                                        {t(
                                            'resumeAssuranceInvaliditeDisabledTexte'
                                        )}
                                    </Typography>
                                )}
                            </div>
                        );
                    })}
                {isModalProtectionVisible && (
                    <Box ml={-3}>
                        <ProtectionModal />
                    </Box>
                )}
            </>
        );
    };

    const renderDejaAssure = () => {
        return (
            <ContainerMui
                dataTestId="bloc_assurance_validation"
                padding={isMobile ? '1rem' : '1.5rem'}
                spacerDistance={6}
                spacerDistanceMobile={5}
            >
                <Typography
                    variant={Variant.h3}
                    police={Police.bold}
                    marginBottom="1rem"
                >
                    {t(obtenirResumeAssuranceTitreSection(resumeOffre))}
                </Typography>
                {renderAssuranceAssure()}
            </ContainerMui>
        );
    };

    const renderAdmissibleSansChoixAssurance = () => {
        return (
            <ContainerMui
                dataTestId="bloc_assurance_validation"
                padding={isMobile ? '1rem' : '1.5rem'}
                spacerDistance={6}
                spacerDistanceMobile={5}
            >
                <Typography variant={Variant.h4} police={Police.bold}>
                    {t(obtenirResumeAssuranceTitreSection(resumeOffre))}
                </Typography>
                <Typography variant={Variant.p}>
                    {t('resumeAssuranceAdmissibleSansChoix')}
                </Typography>
                {isEditButtonDisplayed && (
                    <EditAssuranceButton
                        action={null}
                        libelleHorsEcran={t('modifierProtectionTexteHorsEcran')}
                        onEditAssurance={onEditAssurance}
                    />
                )}
            </ContainerMui>
        );
    };

    return (
        <>
            {/* Deja de l'assurance -> On affiche leur statuts */}
            {!isAssuranceAdmissible &&
                hasAlreadyAssurance(resumeOffre?.assurance?.codeAssurance) &&
                renderDejaAssure()}
            {/* Admissible a l'ajout d'assurance et a choisi de l'assurance */}
            {isAssuranceAdmissible &&
                hasSoumissionAssurance(
                    resumeOffre?.assurance?.codeAssurance
                ) && (
                    <ResumeAjoutAssurance
                        resumeOffre={resumeOffre}
                        assures={assures}
                        isEditButtonDisplayed={isEditButtonDisplayed}
                        parcoursAssurance={parcoursAssurance}
                        onEditAssurance={onEditAssurance}
                    />
                )}
            {/* Admissible a l'ajout d'assurance et n'a pas choisi de l'assurance */}
            {/* Visible que sur certaines pages (isNoAssuranceDisplayed) */}
            {isAssuranceAdmissible &&
                isNoAssuranceDisplayed &&
                !hasSoumissionAssurance(
                    resumeOffre?.assurance?.codeAssurance
                ) &&
                renderAdmissibleSansChoixAssurance()}
        </>
    );
};
